import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Send, Loader2, ArrowRight, Radio } from "lucide-react";

/*  THE DISPATCHER — Kairo's post-intro greeter.
    Mounts globally AFTER the TransmissionIntro has been dismissed.
    Shows a dossier-style card lower-left with Kairo's noir voice.
    - Opener line is static (zero LLM cost — most visitors glance & bounce).
    - Each typed turn hits POST /api/dispatcher/turn and gets a routed CTA back.
    - Caps at MAX_TURNS (backend-enforced) so costs stay honest.
    - Dismissible; once dismissed we remember for 7 days.

    Degradation: if the LLM is down, the backend returns `fallback: true`
    with a scripted intent list we render as big buttons instead.
*/

const DISMISS_KEY  = "wm_dispatcher_dismissed_v1";
const DISMISS_HOURS = 24 * 7;    // 7-day cooldown per visitor
const INTRO_SEEN_KEY = "wm_transmission_seen_v1";

const _dismissedRecently = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    const hoursSince = (Date.now() - ts) / 36e5;
    return hoursSince < DISMISS_HOURS;
  } catch { return false; }
};

export const Dispatcher = () => {
  const navigate = useNavigate();
  const loc = useLocation();

  // Mount on landing (/) AND live chat landings (/live/{code}). Skip
  // everywhere else so the overlay doesn't follow visitors into checkout
  // or interrupt a host running a case.
  const liveMatch = loc.pathname.match(/^\/live\/([^/]+)\/?$/i);
  const onLanding = loc.pathname === "/" || loc.pathname === "";
  const onLiveChat = Boolean(liveMatch);
  const streamCode = liveMatch ? liveMatch[1].toUpperCase() : "";

  const [visible, setVisible]     = useState(false);
  const [opener, setOpener]       = useState("");
  const [quickPicks, setQuickPicks] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages]   = useState([]);   // {role, text, route?}
  const [input, setInput]         = useState("");
  const [sending, setSending]     = useState(false);
  const [fallback, setFallback]   = useState(null);  // {intents:[]}
  const [currentRoute, setCurrentRoute] = useState(null);  // {id,path,label}
  const [capped, setCapped]       = useState(false);
  const openedRef = useRef(false);

  // Wait until the Transmission intro has been dismissed before showing up.
  // On /live/{code}, visitors come from a stream — the Transmission intro
  // doesn't play there, so skip the wait and mount immediately.
  useEffect(() => {
    if (!onLanding && !onLiveChat) { setVisible(false); return; }
    if (_dismissedRecently()) return;
    if (onLiveChat) {
      // Live visitors should see Kairo fast — streamer told them to come here
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      try {
        if (localStorage.getItem(INTRO_SEEN_KEY) === "1") {
          setTimeout(() => { if (!cancelled) setVisible(true); }, 900);
        } else {
          setTimeout(check, 400);
        }
      } catch { setVisible(true); }
    };
    check();
    return () => { cancelled = true; };
  }, [onLanding, onLiveChat]);

  // Fetch opener + mint session on first open
  useEffect(() => {
    if (!visible || openedRef.current) return;
    openedRef.current = true;
    const sp = new URLSearchParams(window.location.search);
    const referred_by = sp.get("ref") || sp.get("r") || "";
    let detective_number = null;
    try {
      const stored = localStorage.getItem("wm_detective_number");
      if (stored) detective_number = parseInt(stored, 10) || null;
    } catch {}

    // Mint a Detective # for first-time anonymous visitors (no email required)
    const ensureDetective = async () => {
      if (detective_number) return detective_number;
      try {
        const r = await api.post("/detective/assign", {});
        const n = r.data?.detective_number;
        if (n) {
          try { localStorage.setItem("wm_detective_number", String(n)); } catch {}
          return n;
        }
      } catch {}
      return null;
    };

    ensureDetective().then((num) => {
      api.post("/dispatcher/open", {
        referred_by,
        detective_number: num || detective_number,
        stream_code: streamCode,
      })
        .then((r) => {
          setSessionId(r.data.session_id);
          setOpener(r.data.opener);
          setQuickPicks(r.data.quick_picks || []);
        })
        .catch(() => {
          setOpener("You made it. Not everyone does. Pick a lane.");
        });
    });
  }, [visible, streamCode]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  const sendTurn = async (text) => {
    if (!text || !sessionId || sending || capped) return;
    const userMsg = { role: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/dispatcher/turn", { session_id: sessionId, text });
      const botMsg = {
        role:  "kairo",
        text:  data.reply || "",
        route: data.route_id
          ? { id: data.route_id, path: data.route_path, label: data.route_label }
          : null,
      };
      setMessages((prev) => [...prev, botMsg]);
      if (data.route_id) {
        setCurrentRoute({ id: data.route_id, path: data.route_path, label: data.route_label });
      }
      if (data.capped) setCapped(true);
      if (data.fallback) setFallback({ intents: data.fallback_intents || [] });
    } catch {
      setMessages((prev) => [...prev, { role: "kairo", text: "Comms stuttered. Pick a lane.", route: null }]);
    } finally {
      setSending(false);
    }
  };

  const followRoute = (route) => {
    if (!route?.path) return;
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
    // Internal nav if relative, external if absolute (none currently are)
    navigate(route.path);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-4 sm:left-6 sm:right-auto sm:bottom-6 sm:w-[380px] z-[70] dispatcher-root"
      role="dialog"
      aria-label="Kairo the Dispatcher"
      data-testid="dispatcher-overlay"
    >
      <div className="relative rounded-lg border border-[#8b0000]/40 bg-[#0a0908]/95 backdrop-blur-md shadow-2xl overflow-hidden">
        {/* Scanline accent */}
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#8b0000] to-transparent" />
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2525]">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#8b0000] dispatcher-pulse" />
            <div className="text-[9px] tracking-[0.3em] text-[#d4af37]">COMMS OPEN · KAIRO</div>
          </div>
          <button
            onClick={dismiss}
            className="text-[#5a5050] hover:text-[#d4af37] transition-colors"
            aria-label="Dismiss"
            data-testid="dispatcher-dismiss"
          >
            <X size={14} />
          </button>
        </div>

        {/* Transcript */}
        <div className="px-4 py-4 max-h-[380px] overflow-y-auto dispatcher-scroll space-y-3">
          {/* Static opener */}
          {opener && (
            <div className="flex gap-2" data-testid="dispatcher-opener">
              <div className="w-7 h-7 rounded-full bg-[#8b0000]/20 border border-[#8b0000]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Radio size={12} className="text-[#d4af37]" />
              </div>
              <div className="text-xs text-[#d4d0cc] leading-relaxed">{opener}</div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "kairo" && (
                <div className="w-7 h-7 rounded-full bg-[#8b0000]/20 border border-[#8b0000]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Radio size={12} className="text-[#d4af37]" />
                </div>
              )}
              <div className={m.role === "user"
                ? "max-w-[80%] rounded-md bg-[#1a1515] border border-[#2a2525] px-3 py-2 text-xs text-[#f4f0ec] leading-relaxed"
                : "max-w-[80%] text-xs text-[#d4d0cc] leading-relaxed"
              }>
                {m.text}
                {m.route && (
                  <button
                    onClick={() => followRoute(m.route)}
                    className="mt-2 w-full btn-primary text-[11px] py-2"
                    data-testid={`dispatcher-route-${m.route.id}`}
                  >
                    {m.route.label || "Open"} <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex gap-2 items-center text-[10px] text-[#5a5050]">
              <Loader2 size={10} className="animate-spin" /> Kairo's thinking…
            </div>
          )}

          {capped && !currentRoute && (
            <div className="text-[10px] text-[#5a5050] italic">
              Comms closed. Pick a lane above and move.
            </div>
          )}

          {/* Fallback scripted buttons if the LLM is down */}
          {fallback?.intents && fallback.intents.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-[#2a2525]">
              {fallback.intents.map((f) => (
                <button
                  key={f.id}
                  onClick={() => followRoute({ id: f.id, path: f.path, label: f.cta })}
                  className="w-full text-left text-xs text-[#d4d0cc] bg-[#1a1515]/60 hover:bg-[#1a1515] border border-[#2a2525] hover:border-[#8b0000]/40 rounded-md px-3 py-2 transition-colors"
                  data-testid={`dispatcher-fallback-${f.id}`}
                >
                  {f.cta}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick picks (only shown before the first turn) */}
        {!capped && messages.length === 0 && quickPicks.length > 0 && (
          <div className="px-4 pb-3 space-y-1.5 border-t border-[#2a2525] pt-3" data-testid="dispatcher-quickpicks">
            {quickPicks.slice(0, 4).map((q, i) => (
              <button
                key={i}
                onClick={() => sendTurn(q.intent)}
                disabled={sending}
                className="w-full text-left text-xs text-[#d4d0cc] bg-[#1a1515]/60 hover:bg-[#1a1515] border border-[#2a2525] hover:border-[#8b0000]/40 rounded-md px-3 py-2 transition-colors disabled:opacity-50"
                data-testid={`dispatcher-quickpick-${i}`}
              >
                ▸ {q.label}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        {!capped && (
          <form
            onSubmit={(e) => { e.preventDefault(); sendTurn(input.trim()); }}
            className="flex items-center gap-2 px-3 py-3 border-t border-[#2a2525] bg-[#0a0908]"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your angle..."
              className="flex-1 bg-transparent text-xs text-[#f4f0ec] placeholder:text-[#5a5050] focus:outline-none"
              disabled={sending}
              maxLength={300}
              data-testid="dispatcher-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="text-[#d4af37] hover:text-[#f4f0ec] disabled:opacity-30 transition-colors"
              aria-label="Send"
              data-testid="dispatcher-send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Dispatcher;
